import { motion } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import LightbulbIcon from '../components/icons/LightbulbIcon'
import YogaIcon from '../components/icons/YogaIcon'
import StartupIcon from '../components/icons/StartupIcon'
import SEO from '../components/SEO'
import { generateOrganizationSchema } from '../lib/structured-data-utils'

interface Career {
  id: string
  title: string
  department: string
  location: string
  type: string
  description: string
  requirements: string[]
  responsibilities: string[]
}

const Careers = () => {
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null)
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Static positions data
  const positions: Career[] = [
    {
      id: 'mobile-flutter-developer',
      title: 'Mobile Flutter Developer',
      department: 'Engineering',
      location: 'Niš, Serbia',
      type: 'Full-time',
      description: 'We are seeking an experienced Mobile Flutter Developer to design and build advanced applications for iOS and Android platforms. Join our mobile team in Niš to create beautiful, performant cross-platform applications for our clients.',
      requirements: [
        '3+ years of mobile development experience with Flutter/Dart',
        'Strong understanding of Flutter framework, widgets, and state management (Provider, Riverpod, or Bloc)',
        'Experience with native iOS (Swift) and Android (Kotlin) development',
        'Proficiency in RESTful APIs and third-party integrations',
        'Experience with Firebase services (Authentication, Firestore, Cloud Functions)',
        'Knowledge of mobile app deployment (App Store and Play Store)',
        'Familiarity with CI/CD pipelines for mobile applications',
        'Understanding of responsive design and adaptive layouts',
        'Excellent English communication skills'
      ],
      responsibilities: [
        'Develop and maintain high-quality Flutter applications for iOS and Android',
        'Collaborate with designers to implement pixel-perfect UIs',
        'Integrate with backend APIs and third-party services',
        'Write comprehensive unit and widget tests',
        'Optimize application performance and memory usage',
        'Implement state management solutions and app architecture',
        'Review code and provide constructive feedback to team members',
        'Stay updated with latest Flutter and mobile development trends'
      ]
    },
    {
      id: 'flutter-qa-engineer',
      title: 'Flutter QA Engineer',
      department: 'Engineering',
      location: 'Niš, Serbia',
      type: 'Full-time',
      description: 'We are looking for a Flutter QA Engineer to ensure the quality and reliability of our cross-platform mobile applications. Join our team in Niš to design and execute comprehensive testing strategies for Flutter apps running on iOS and Android.',
      requirements: [
        '2+ years of QA experience with mobile applications',
        'Hands-on experience testing Flutter/Dart applications',
        'Proficiency with Flutter testing frameworks (flutter_test, integration_test)',
        'Experience writing automated tests using tools like Appium, Patrol, or Maestro',
        'Strong understanding of manual and exploratory testing on iOS and Android devices',
        'Familiarity with REST API testing tools (Postman, Charles Proxy, or similar)',
        'Experience with bug tracking and test management tools (Jira, TestRail)',
        'Knowledge of CI/CD pipelines and integrating automated tests',
        'Understanding of mobile-specific testing concerns (performance, battery, network)',
        'Excellent English communication skills'
      ],
      responsibilities: [
        'Design, write, and maintain automated test suites for Flutter applications',
        'Perform manual testing across iOS and Android devices and OS versions',
        'Develop end-to-end and integration tests using Flutter testing frameworks',
        'Collaborate with developers to define test cases and acceptance criteria',
        'Identify, document, and track bugs through resolution',
        'Test API integrations and backend services consumed by mobile apps',
        'Validate UI/UX consistency against design specifications',
        'Integrate automated tests into CI/CD pipelines',
        'Perform regression, performance, and usability testing',
        'Contribute to QA processes and continuously improve test coverage'
      ]
    },
    {
      id: 'senior-fullstack-web3-engineer',
      title: 'Senior Fullstack Web3 Engineer',
      department: 'Engineering',
      location: 'Niš, Serbia',
      type: 'Full-time',
      description: 'We are looking for a Senior Fullstack Web3 Engineer to build cutting-edge decentralized applications and blockchain solutions. Join our team in Niš to work on innovative Web3 projects and shape the future of decentralized technology.',
      requirements: [
        '5+ years of fullstack development experience',
        'Strong expertise in React, TypeScript, and Node.js',
        '2+ years of hands-on experience with Web3 technologies and blockchain development',
        'Proficiency in Solidity and smart contract development (Ethereum, Polygon, or similar)',
        'Experience with Web3 libraries (ethers.js, web3.js, wagmi, viem)',
        'Understanding of DeFi protocols, NFTs, and token standards (ERC-20, ERC-721, ERC-1155)',
        'Knowledge of wallet integration (MetaMask, WalletConnect, Coinbase Wallet)',
        'Experience with IPFS, The Graph, or other decentralized storage/indexing solutions',
        'Familiarity with testing frameworks for smart contracts (Hardhat, Foundry)',
        'Strong understanding of blockchain security best practices',
        'Excellent English communication skills'
      ],
      responsibilities: [
        'Design and develop full-stack decentralized applications (dApps)',
        'Write, test, and deploy secure smart contracts on EVM-compatible chains',
        'Build intuitive Web3 user interfaces with wallet connectivity',
        'Integrate with blockchain networks, smart contracts, and decentralized protocols',
        'Implement backend services for blockchain indexing and data aggregation',
        'Conduct security audits and implement best practices for smart contract security',
        'Optimize gas efficiency and transaction performance',
        'Collaborate with cross-functional teams on Web3 architecture and strategy',
        'Mentor junior developers and contribute to technical documentation',
        'Stay current with emerging Web3 technologies and ecosystem developments'
      ]
    }
  ]

  const togglePosition = (positionId: string) => {
    setSelectedPosition(selectedPosition === positionId ? null : positionId)
  }

  return (
    <>
      <SEO
        title="Careers at Demirix - Join Our IT Team in Dubai & Niš"
        description="Explore exciting career opportunities at Demirix. Join our innovative team and help build the future of technology in Dubai."
        keywords={['tech careers Dubai', 'IT jobs UAE', 'software engineering positions', 'design jobs Dubai']}
        url={currentUrl}
        structuredData={generateOrganizationSchema()}
      />
      <div className="min-h-screen bg-white dark:bg-gray-900 pt-20 pb-12">
        <div className="container-custom">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Build your career with Demirix and be part of a team that's shaping
              the future of technology in Dubai and beyond.
            </p>
          </motion.div>

          {/* Values Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8"
            >
              <div className="flex justify-center mb-4">
                <LightbulbIcon className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
                Innovation First
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                We encourage creative thinking and provide the tools and freedom to innovate.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8"
            >
              <div className="flex justify-center mb-4">
                <YogaIcon className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
                Work-Life Balance
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                Flexible hours and remote options to help you maintain a healthy balance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8"
            >
              <div className="flex justify-center mb-4">
                <StartupIcon className="w-12 h-12" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
                Growth & Learning
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                Continuous learning opportunities and career development programs.
              </p>
            </motion.div>
          </div>

          {/* Open Positions */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Open Positions
            </h2>

            {positions.length > 0 ? (
              <div className="space-y-4">
                {positions.map((position) => (
                  <motion.div
                    key={position.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                  >
                    <button
                      onClick={() => togglePosition(position.id)}
                      className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <div className="text-left">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                          {position.title}
                        </h3>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {position.department}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {position.location}
                          </span>
                          <span className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {position.type}
                          </span>
                        </div>
                      </div>
                      <svg
                        className={`w-6 h-6 text-gray-400 transform transition-transform duration-200 ${
                          selectedPosition === position.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {selectedPosition === position.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-8 py-6 border-t border-gray-100 dark:border-gray-700"
                      >
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                              About the Role
                            </h4>
                            <p className="text-gray-600 dark:text-gray-300">
                              {position.description}
                            </p>
                          </div>

                          {position.requirements && position.requirements.length > 0 && (
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Requirements
                              </h4>
                              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                                {position.requirements.map((req, index) => (
                                  <li key={index}>{req}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {position.responsibilities && position.responsibilities.length > 0 && (
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                Responsibilities
                              </h4>
                              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                                {position.responsibilities.map((resp, index) => (
                                  <li key={index}>{resp}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <Link
                            to={`/apply?position=${encodeURIComponent(position.title)}`}
                            className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                          >
                            Apply for this Position
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-12"
              >
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  No open positions at the moment, but we're always looking for talented individuals!
                </p>
                <Link
                  to="/contact"
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Send us your resume →
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Careers